import React, { useState, useEffect, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import { useAuth } from '../../context/AuthContext';
import { toast } from 'sonner';
import { Card, CardContent, CardHeader, CardTitle } from '../../components/ui/card';
import { Button } from '../../components/ui/button';
import { Badge } from '../../components/ui/badge';
import { ScrollArea } from '../../components/ui/scroll-area';
import {
  ArrowLeft, Users, Wifi, WifiOff, Activity, Clock,
  AlertTriangle, Radio, RefreshCw, Gauge, MonitorCheck,
  Globe, Shield, Layers, ChevronRight, Zap,
} from 'lucide-react';
import ConnectionsSubNav from '../../components/hub/ConnectionsSubNav';

const API_URL = process.env.REACT_APP_BACKEND_URL + '/api';
const getHeaders = () => ({
  'Content-Type': 'application/json',
  Authorization: `Bearer ${localStorage.getItem('ems_token')}`,
});

const formatTimeAgo = (isoStr) => {
  if (!isoStr) return '—';
  const diff = (Date.now() - new Date(isoStr).getTime()) / 1000;
  if (diff < 60) return `${Math.floor(diff)}s ago`;
  if (diff < 3600) return `${Math.floor(diff / 60)}m ago`;
  if (diff < 86400) return `${Math.floor(diff / 3600)}h ago`;
  return `${Math.floor(diff / 86400)}d ago`;
};

const LatencyBadge = ({ ms }) => {
  if (ms === null || ms === undefined) return <Badge variant="outline" className="text-xs">—</Badge>;
  const color = ms < 100 ? 'bg-emerald-500/15 text-emerald-600 border-emerald-500/30'
    : ms < 300 ? 'bg-amber-500/15 text-amber-600 border-amber-500/30'
    : 'bg-red-500/15 text-red-600 border-red-500/30';
  return <Badge className={`text-xs font-mono ${color}`}>{Math.round(ms)}ms</Badge>;
};

const StatusDot = ({ alive }) => (
  <span className={`inline-block w-2 h-2 rounded-full ${alive ? 'bg-emerald-500 animate-pulse' : 'bg-red-500'}`} />
);

export default function ConnectionHealthPage() {
  const navigate = useNavigate();
  const { user } = useAuth();
  const [data, setData] = useState(null);
  const [disconnections, setDisconnections] = useState(null);
  const [loading, setLoading] = useState(true);
  const [autoRefresh, setAutoRefresh] = useState(true);
  // V14.11.4 — Map exercise / MP IDs to human-readable names so rooms
  // like `exercise:ex_c6acdfeceb2a` render as `Exercise Trident`.
  const [nameMap, setNameMap] = useState({});

  // Load name maps once (cheap; ~3-4 docs each)
  useEffect(() => {
    const headers = getHeaders();
    Promise.all([
      fetch(`${API_URL}/exercises`, { headers }).then(r => r.ok ? r.json() : []).catch(() => []),
      fetch(`${API_URL}/mp/exercises`, { headers }).then(r => r.ok ? r.json() : []).catch(() => []),
    ]).then(([ems, mp]) => {
      const map = {};
      (Array.isArray(ems) ? ems : []).forEach(e => { if (e.exercise_id) map[e.exercise_id] = e.name || e.exercise_id; });
      (Array.isArray(mp) ? mp : []).forEach(e => { if (e.exercise_id) map[e.exercise_id] = e.name || e.exercise_id; });
      setNameMap(map);
    });
  }, []);

  // Resolve `exercise:ex_xxx` / `mp_exercise:mpex_xxx` / `guest:tok_xxx` to a
  // friendly label. Falls back to the raw room ID when the entity is unknown
  // (e.g. an exercise was deleted but a socket is still in its room).
  const friendlyRoom = useCallback((room) => {
    if (!room) return '';
    const idx = room.indexOf(':');
    if (idx === -1) return room;
    const prefix = room.slice(0, idx);
    const id = room.slice(idx + 1);
    if (prefix === 'guest') return `Guest · ${id.slice(0, 10)}…`;
    return nameMap[id] || id;
  }, [nameMap]);

  const fetchData = useCallback(async () => {
    try {
      const [connRes, discRes] = await Promise.all([
        fetch(`${API_URL}/hub/connections`, { headers: getHeaders() }),
        fetch(`${API_URL}/hub/connections/disconnections?limit=30`, { headers: getHeaders() }),
      ]);
      if (connRes.ok) setData(await connRes.json());
      if (discRes.ok) setDisconnections(await discRes.json());
    } catch (err) {
      toast.error('Failed to fetch connection data');
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    fetchData();
  }, [fetchData]);

  useEffect(() => {
    if (!autoRefresh) return;
    const interval = setInterval(fetchData, 5000);
    return () => clearInterval(interval);
  }, [autoRefresh, fetchData]);

  if (loading || !data) {
    return (
      <div className="min-h-screen bg-background flex items-center justify-center">
        <RefreshCw className="w-6 h-6 animate-spin text-muted-foreground" />
      </div>
    );
  }

  const { total_connected, total_users, total_guests, connections, rooms, latency, role_distribution } = data;
  const discAlert = disconnections?.alert;

  return (
    <div className="min-h-screen bg-background" data-testid="connection-health-page">
      {/* Header */}
      <div className="border-b border-border bg-card/50 backdrop-blur-sm sticky top-0 z-10">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 py-3 flex items-center justify-between">
          <div className="flex items-center gap-3">
            <Button variant="ghost" size="icon" onClick={() => navigate('/hub/system')} data-testid="back-btn">
              <ArrowLeft className="w-4 h-4" />
            </Button>
            <div>
              <h1 className="text-lg font-bold flex items-center gap-2">
                <Radio className="w-4 h-4 text-emerald-500" />
                Connection Health
              </h1>
              <p className="text-xs text-muted-foreground">Real-time Socket.IO monitoring</p>
            </div>
          </div>
          <div className="flex items-center gap-2">
            <Button
              variant={autoRefresh ? 'default' : 'outline'}
              size="sm"
              onClick={() => setAutoRefresh(!autoRefresh)}
              data-testid="auto-refresh-toggle"
            >
              <Activity className={`w-3.5 h-3.5 mr-1.5 ${autoRefresh ? 'animate-pulse' : ''}`} />
              {autoRefresh ? 'Live' : 'Paused'}
            </Button>
            <Button variant="outline" size="sm" onClick={fetchData} data-testid="refresh-btn">
              <RefreshCw className="w-3.5 h-3.5" />
            </Button>
          </div>
        </div>
      </div>

      <ConnectionsSubNav />

      <div className="max-w-7xl mx-auto px-4 sm:px-6 py-6 space-y-6">
        {/* Disconnection Alert Banner */}
        {discAlert && (
          <div
            className="flex items-center gap-3 px-4 py-3 rounded-lg bg-red-500/10 border border-red-500/30 text-red-600 dark:text-red-400"
            data-testid="disconnection-alert"
          >
            <AlertTriangle className="w-5 h-5 shrink-0" />
            <div>
              <p className="text-sm font-semibold">Disconnection Spike Detected</p>
              <p className="text-xs">{disconnections.alert_message}</p>
            </div>
          </div>
        )}

        {/* Summary Cards */}
        <div className="grid grid-cols-2 sm:grid-cols-4 gap-4">
          <Card data-testid="total-connected-card">
            <CardContent className="p-4">
              <div className="flex items-center justify-between">
                <div>
                  <p className="text-xs text-muted-foreground uppercase tracking-wider">Connected</p>
                  <p className="text-3xl font-bold mt-1">{total_connected}</p>
                </div>
                <div className="w-10 h-10 rounded-lg bg-emerald-500/10 flex items-center justify-center">
                  <Wifi className="w-5 h-5 text-emerald-500" />
                </div>
              </div>
              <div className="flex items-center gap-2 mt-2 text-xs text-muted-foreground">
                <Users className="w-3 h-3" /> {total_users} users
                <Globe className="w-3 h-3 ml-1" /> {total_guests} guests
              </div>
            </CardContent>
          </Card>

          <Card data-testid="avg-latency-card">
            <CardContent className="p-4">
              <div className="flex items-center justify-between">
                <div>
                  <p className="text-xs text-muted-foreground uppercase tracking-wider">Avg Latency</p>
                  <p className="text-3xl font-bold mt-1">
                    {latency.average_ms !== null ? `${Math.round(latency.average_ms)}` : '—'}
                    <span className="text-sm font-normal text-muted-foreground ml-0.5">ms</span>
                  </p>
                </div>
                <div className="w-10 h-10 rounded-lg bg-blue-500/10 flex items-center justify-center">
                  <Gauge className="w-5 h-5 text-blue-500" />
                </div>
              </div>
              <div className="flex items-center gap-3 mt-2 text-xs text-muted-foreground">
                <span>Min: {latency.min_ms ?? '—'}ms</span>
                <span>Max: {latency.max_ms ?? '—'}ms</span>
              </div>
            </CardContent>
          </Card>

          <Card data-testid="active-rooms-card">
            <CardContent className="p-4">
              <div className="flex items-center justify-between">
                <div>
                  <p className="text-xs text-muted-foreground uppercase tracking-wider">Active Rooms</p>
                  <p className="text-3xl font-bold mt-1">
                    {Object.keys(rooms.exercise).length + Object.keys(rooms.mp).length}
                  </p>
                </div>
                <div className="w-10 h-10 rounded-lg bg-violet-500/10 flex items-center justify-center">
                  <Layers className="w-5 h-5 text-violet-500" />
                </div>
              </div>
              <div className="flex items-center gap-3 mt-2 text-xs text-muted-foreground">
                <span>{Object.keys(rooms.exercise).length} exercise</span>
                <span>{Object.keys(rooms.mp).length} MP</span>
              </div>
            </CardContent>
          </Card>

          <Card data-testid="disconnections-card">
            <CardContent className="p-4">
              <div className="flex items-center justify-between">
                <div>
                  <p className="text-xs text-muted-foreground uppercase tracking-wider">Disconnects (5m)</p>
                  <p className={`text-3xl font-bold mt-1 ${disconnections?.recent_5min_count > 5 ? 'text-red-500' : ''}`}>
                    {disconnections?.recent_5min_count ?? 0}
                  </p>
                </div>
                <div className={`w-10 h-10 rounded-lg flex items-center justify-center ${
                  disconnections?.recent_5min_count > 5 ? 'bg-red-500/10' : 'bg-muted/50'
                }`}>
                  <WifiOff className={`w-5 h-5 ${disconnections?.recent_5min_count > 5 ? 'text-red-500' : 'text-muted-foreground'}`} />
                </div>
              </div>
              <div className="flex items-center gap-2 mt-2 text-xs text-muted-foreground">
                <StatusDot alive={!discAlert} />
                {discAlert ? 'Elevated' : 'Normal'}
              </div>
            </CardContent>
          </Card>
        </div>

        <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
          {/* Connected Users Table */}
          <div className="lg:col-span-2">
            <Card>
              <CardHeader className="pb-3">
                <CardTitle className="text-base flex items-center gap-2">
                  <Users className="w-4 h-4" />
                  Connected Users
                  <Badge variant="outline" className="ml-auto text-xs">{connections.length}</Badge>
                </CardTitle>
              </CardHeader>
              <CardContent className="p-0">
                <ScrollArea className="h-[420px]">
                  <div className="divide-y divide-border" data-testid="connections-list">
                    {connections.length === 0 ? (
                      <div className="p-8 text-center text-sm text-muted-foreground">No active connections</div>
                    ) : connections.map((conn, i) => (
                      <div key={conn.sid || i} className="flex items-center gap-3 px-4 py-2.5 hover:bg-muted/30 transition-colors" data-testid={`connection-row-${i}`}>
                        <StatusDot alive />
                        <div className="flex-1 min-w-0">
                          <div className="flex items-center gap-2">
                            <span className="text-sm font-medium truncate">
                              {conn.is_guest ? 'Guest' : conn.name || conn.user_id}
                            </span>
                            {conn.roles?.map(r => (
                              <Badge key={r} variant="outline" className="text-[10px] py-0">
                                {r}
                              </Badge>
                            ))}
                          </div>
                          <div className="flex items-center gap-3 text-[11px] text-muted-foreground mt-0.5">
                            <span className="flex items-center gap-1">
                              <Clock className="w-2.5 h-2.5" /> {formatTimeAgo(conn.connected_at)}
                            </span>
                            {conn.rooms?.filter(r => !r.startsWith('user:')).length > 0 && (
                              <span className="flex items-center gap-1">
                                <Layers className="w-2.5 h-2.5" />
                                {conn.rooms.filter(r => !r.startsWith('user:')).map(friendlyRoom).join(', ')}
                              </span>
                            )}
                          </div>
                        </div>
                        <LatencyBadge ms={conn.latency_ms} />
                      </div>
                    ))}
                  </div>
                </ScrollArea>
              </CardContent>
            </Card>
          </div>

          {/* Right Column */}
          <div className="space-y-6">
            {/* Active Rooms */}
            <Card>
              <CardHeader className="pb-3">
                <CardTitle className="text-base flex items-center gap-2">
                  <Layers className="w-4 h-4" />
                  Active Rooms
                </CardTitle>
              </CardHeader>
              <CardContent className="space-y-3" data-testid="rooms-list">
                {Object.entries(rooms.exercise).length === 0 && Object.entries(rooms.mp).length === 0 ? (
                  <p className="text-xs text-muted-foreground text-center py-4">No active rooms</p>
                ) : (
                  <>
                    {Object.entries(rooms.exercise).map(([room, count]) => (
                      <div key={room} className="flex items-center justify-between text-sm gap-3">
                        <div className="flex items-center gap-2 min-w-0">
                          <MonitorCheck className="w-3.5 h-3.5 text-emerald-500 shrink-0" />
                          <div className="min-w-0">
                            <div className="text-sm font-medium truncate">{friendlyRoom(room)}</div>
                            <div className="font-mono text-[10px] text-muted-foreground truncate">{room}</div>
                          </div>
                        </div>
                        <Badge variant="secondary" className="text-xs shrink-0">{count} users</Badge>
                      </div>
                    ))}
                    {Object.entries(rooms.mp).map(([room, count]) => (
                      <div key={room} className="flex items-center justify-between text-sm gap-3">
                        <div className="flex items-center gap-2 min-w-0">
                          <Globe className="w-3.5 h-3.5 text-blue-500 shrink-0" />
                          <div className="min-w-0">
                            <div className="text-sm font-medium truncate">{friendlyRoom(room)}</div>
                            <div className="font-mono text-[10px] text-muted-foreground truncate">{room}</div>
                          </div>
                        </div>
                        <Badge variant="secondary" className="text-xs shrink-0">{count} users</Badge>
                      </div>
                    ))}
                    {Object.entries(rooms.guest).map(([room, count]) => (
                      <div key={room} className="flex items-center justify-between text-sm gap-3">
                        <div className="flex items-center gap-2 min-w-0">
                          <Globe className="w-3.5 h-3.5 text-amber-500 shrink-0" />
                          <div className="min-w-0">
                            <div className="text-sm font-medium truncate">{friendlyRoom(room)}</div>
                            <div className="font-mono text-[10px] text-muted-foreground truncate">{room}</div>
                          </div>
                        </div>
                        <Badge variant="secondary" className="text-xs shrink-0">{count} guests</Badge>
                      </div>
                    ))}
                  </>
                )}
              </CardContent>
            </Card>

            {/* Role Distribution */}
            <Card>
              <CardHeader className="pb-3">
                <CardTitle className="text-base flex items-center gap-2">
                  <Shield className="w-4 h-4" />
                  Role Distribution
                </CardTitle>
              </CardHeader>
              <CardContent className="space-y-2" data-testid="role-distribution">
                {Object.keys(role_distribution).length === 0 ? (
                  <p className="text-xs text-muted-foreground text-center py-4">No role data</p>
                ) : Object.entries(role_distribution).map(([role, count]) => (
                  <div key={role} className="flex items-center justify-between">
                    <span className="text-sm capitalize">{role.replace('_', ' ')}</span>
                    <div className="flex items-center gap-2">
                      <div className="w-20 h-1.5 rounded-full bg-muted overflow-hidden">
                        <div
                          className="h-full rounded-full bg-foreground/30"
                          style={{ width: `${Math.min(100, (count / total_connected) * 100)}%` }}
                        />
                      </div>
                      <span className="text-xs font-mono text-muted-foreground w-6 text-right">{count}</span>
                    </div>
                  </div>
                ))}
              </CardContent>
            </Card>

            {/* Recent Disconnections */}
            <Card>
              <CardHeader className="pb-3">
                <CardTitle className="text-base flex items-center gap-2">
                  <WifiOff className="w-4 h-4" />
                  Recent Disconnections
                  {discAlert && <AlertTriangle className="w-3.5 h-3.5 text-red-500 ml-auto" />}
                </CardTitle>
              </CardHeader>
              <CardContent className="p-0">
                <ScrollArea className="h-[180px]">
                  <div className="divide-y divide-border" data-testid="disconnections-list">
                    {(!disconnections?.disconnections || disconnections.disconnections.length === 0) ? (
                      <div className="p-6 text-center text-xs text-muted-foreground">No recent disconnections</div>
                    ) : disconnections.disconnections.map((d, i) => (
                      <div key={i} className="flex items-center gap-2 px-4 py-2 text-xs">
                        <WifiOff className="w-3 h-3 text-red-400 shrink-0" />
                        <span className="font-medium truncate">{d.name || d.user_id}</span>
                        <span className="text-muted-foreground ml-auto whitespace-nowrap">
                          {formatTimeAgo(d.disconnected_at)}
                        </span>
                      </div>
                    ))}
                  </div>
                </ScrollArea>
              </CardContent>
            </Card>
          </div>
        </div>
      </div>
    </div>
  );
}
